import { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import machines from '../lib/machines';
import Copy from '../assets/images/copy-icon.svg'

const Translator = ({defaultMachine, program}) => {
    const [axis, setAxis] = useState(false);
    const [machine, setMachine] = useState(defaultMachine ? machines.filter(item => item.value === defaultMachine)[0] : null);
    const [code, setCode] = useState(program || '');
    const [comments, setComments] = useState('');
    const [mapping, setMapping] = useState(null);
    const [isSticky, setIsSticky] = useState(false)
    const lastNumber = useRef(null);
    const lastCommand = useRef(null);
    const header = useRef(null);
    const initialTranslateDone = useRef(false);

    const customStyles = {
        container: (provided, state) => ({
            ...provided,
            flex: '1',
            height: '42px'
          }),
        control: (provided, state) => ({
          ...provided,
          border: '1px solid #D0D5DD',
          '&:hover': {
            boxShadow: '0 0 1px #5D3FC5'
          }
        })
    }

    const translateToken = (token, axisCodes) => {
        token = token.trim();
        if (!token) return '';
      
        if (token.startsWith('(') && token.endsWith(')'))
          return token.trim();
      
        const match = token.match(/^([A-Z%#/])([+-]?[0-9.]+)?(.*)$/i);
        if (match) {
          let letter = match[1].toUpperCase();
          let numberPart = parseFloat(match[2]);

          if (mapping.moveCommands.includes(letter) && !axis && !axisCodes)
            return token;
      
          // any extra characters (like trailing punctuation) are stored in 'rest'
          let rest = match[3] ? match[3].trim() : '';
      
          let cmdMapping = '';

      
          if (lastCommand.current) {
              cmdMapping = mapping.commands[lastCommand.current][lastNumber.current] && mapping.commands[lastCommand.current][lastNumber.current].sublevels && mapping.commands[lastCommand.current][lastNumber.current].sublevels[letter];
            
            if (!cmdMapping)
              cmdMapping = mapping.commands[lastCommand.current].default && mapping.commands[lastCommand.current].default.sublevels[letter];
          }
      
          if (!cmdMapping)
            cmdMapping = mapping.commands && mapping.commands[letter];

          if (cmdMapping && cmdMapping.main && (cmdMapping[numberPart] && cmdMapping[numberPart].exclude ? false : true)) {
              lastCommand.current = letter;
              lastNumber.current = numberPart;
          }
          
          // If still not found, return the token with a note.
          if (!cmdMapping) {
            return token + " (Unknown command)";
          }
      
          // Determine the translation:
          let translation = '';
      
          // If options exist, try to use the number as a key.
          if (typeof cmdMapping === 'object') {
            if (numberPart > 99 && (cmdMapping.offset || cmdMapping.offset00)) {
              const codes = splitNumber(numberPart);
              translation = (codes.lastPart === '00' ? cmdMapping.offset00 : cmdMapping.offset).value
                .replace('{codeValue1}', codes.firstPart)
                .replace('{codeValue2}', codes.lastPart);
            } else
              translation = (cmdMapping[numberPart] || cmdMapping.default || { value: token }).value || (axisCodes ? 'Default values' : 'NOT A ' + mapping.manufacturer + ' ' + mapping.model + ' ' + mapping.controller + ' ' + mapping['years-made'] + ' ' + letter + ' CODE)');
          } else {
            translation = cmdMapping;
          }
      
          // Replace placeholder tokens (if they exist) with the numeric value.
          if (translation) 
            translation = numberPart ? translation.replace('{codeValue}', numberPart) : translation.replace(': {codeValue}', '').replace('{codeValue}', '');
      
          // Append any extra characters back (for example, a trailing period)
          if (rest) {
            translation += ' ' + rest;
          }
          return token + ': ' + translation;
        }
      
        // If token doesn't match a command pattern, return it as is.
        return token;
    }

    const splitNumber = (num) => {
      const str = num.toString();
      const lastPart = str.slice(-2);
      const firstPart = str.slice(0, str.length - 2);
      return { firstPart, lastPart };
    }

    const loadMachine = async (value) => {
      const data = await import('../lib/machines/'+value.value+'.json');
      setMapping(data);
      
      if (value.label)
        setMachine(value);
    }
    
    const translateCNC = async (event, codes) => {
      if (event)
        event.preventDefault();
        
        if ((!code && !codes) || !mapping)
            return;

        lastCommand.current = null;
        lastNumber.current = null;
        setComments('');

        const lines = (codes || code).split('\n');


        const translatedLines = lines.map(line => {
          const trimmed = line.trim();
          if (!trimmed) 
            return ''; 
      
          // Split each line into tokens (by whitespace)
          const tokens = trimmed.replace('• ', '').match(/\(.*?\)|[^\s]+/g);
          // Translate each token and join them with a separator (here using " | " for clarity)
          return trimmed + ' (' + tokens.map(token => translateToken(token, event ? false : true)).join(' ') + ')';
        });

        
      
        setComments(translatedLines.join('\n'));
    }

    const showCodes = () => {
	    let s = "";
      let offsets = {
        'offset00': '100',
        'offset': '1010'
      };

      Object.keys(mapping.commands).forEach(key => {
        Object.keys(mapping.commands[key]).forEach(gkey => {
          if (gkey === 'main')
            return;
          
          s += key+(!isNaN(gkey) ? gkey : gkey.indexOf('offset') >= 0 ? offsets[gkey] : '')+"\n";
          if (mapping.commands[key][gkey].sublevels) {
            Object.keys(mapping.commands[key][gkey].sublevels).forEach(subkey => {
              s += "• " + subkey+"\n";
            });
          }
        });
        s += "\n";
      });

      setCode(s);
      translateCNC(null, s);
    }

    const copy = (type) => {
      navigator.clipboard.writeText(type === 'code' ? code : comments);
    }

    useEffect(() => {
      if (defaultMachine)
        loadMachine({value: defaultMachine})

      const observer = new IntersectionObserver(
        ([event]) => setIsSticky(event.intersectionRatio < 1),
        {threshold: [1], rootMargin: '-1px 0px 0px 0px',}
      )
      observer.observe(header.current)

      

      return () => observer.disconnect()
    }, [defaultMachine, header, isSticky]);

    useEffect(() => {
      if (mapping && program && !initialTranslateDone.current) {
        initialTranslateDone.current = true;
        translateCNC({preventDefault: () => {}});
      }
    }, [mapping, program]);

    return <>
        <form onSubmit={translateCNC} ref={header}>
            <div className="form-column">
                <label>Machine</label>
                <div>
                  <Select options={machines} styles={customStyles} className="select" value={machine} onChange={(value) => loadMachine(value)}  />
                  {mapping && <button type="button" onClick={showCodes}>Show all G-codes</button>}
                </div>
            </div>
            <div className="form-column checkbox">
                <input type="checkbox" name="axis" checked={axis} onChange={() => setAxis(!axis)} id="axis" />
                <label htmlFor="axis">Comment Axis Positions</label>
            </div>
            <div className="buttons">
                <button disabled={!mapping || !code}>Comment code</button>
                {comments && <button type="button" className="default" onClick={() => setComments('')}>Clear</button>}
            </div>
        </form>
        <div className="code">
            <div>
              <div contentEditable={true} onBlur={(e) => setCode(e.target.innerText)} suppressContentEditableWarning={true}>{code}</div>
              {code.trim() && <span className="copy">
                <img src={Copy} alt="Copy to Clipboard" onClick={() => copy('code')} />
              </span>}
            </div>
            <div>
              <div contentEditable={true} onBlur={(e) => setComments(e.target.innerText)} suppressContentEditableWarning={true}>{comments}</div>
              {comments.trim() && <span className="copy">
                <img src={Copy} alt="Copy to Clipboard" onClick={() => copy('comments')} />
              </span>}
            </div>
        </div>
        <div className={`header-form ${isSticky ? 'sticky' : ''}`}>
          <button disabled={!mapping || !code} onClick={translateCNC}>Comment code</button>
          {comments && <button type="button" className="default" onClick={() => setComments('')}>Clear</button>}
        </div>
    </>
}

export default Translator;